// Helpers for the admin's slide preview (pages/lms/LmsModulePreviewPage.tsx).
//
// The preview mirrors how the learner's app shows a module, so the rules here are
// ports of the app's own (apps/mobile/src/screens/lms/LmsContentScreen.tsx and
// apps/mobile/src/components/LmsYoutubePlayer.tsx). Keep them in step: an admin
// checking a module on the web should see what a learner will.

export interface LmsQuizOption {
  id: string;
  text: string;
  is_correct: boolean;
}

/** One slide as the preview shows it: only what a learner can see. */
export interface LmsPreviewSlide {
  id: string;
  slide_type: string;
  title: string;
  body_text: string;
  media_url: string;
  video_embed_url: string;
  background_color: string | null;
  quiz_question: string;
  quiz_options: LmsQuizOption[];
  quiz_explanation: string;
}

/** Video id from watch?v=, youtu.be/, /embed/, /shorts/ and /live/ links (the app's youtubeId). */
export function youtubeId(url: string): string | null {
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /\/embed\/([A-Za-z0-9_-]{11})/,
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    /\/live\/([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = re.exec(url);
    if (m?.[1]) return m[1];
  }
  return null;
}

/**
 * The embedded player for a YouTube video id, with the app's player settings (no
 * related videos from other channels, modest branding). youtube-nocookie.com sets no
 * cookies until the video is played.
 */
export const youtubeEmbedUrl = (videoId: string): string =>
  `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?rel=0&modestbranding=1&playsinline=1`;

/**
 * The colour a text slide is painted, or null for none. '#FFFFFF' is the lms_slides
 * column default, not an authored colour, so those slides stay on the plain slide area.
 */
export function slideBackground(color: string | null | undefined): string | null {
  const c = typeof color === 'string' ? color.trim() : '';
  if (!c || /^#?(fff|ffffff)$/i.test(c)) return null;
  return c;
}

/** Whether light text is needed on this background (the app's isDarkColor). */
export function isDarkColor(color: string): boolean {
  const hex = color.trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return false;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b < 150;
}

export const isQuizSlideType = (type: string): boolean => type.startsWith('quiz_');

/** The instruction line under a quiz question, worded as in the app. */
export const quizHint = (type: string): string =>
  type === 'quiz_multiple' ? 'Choose all that apply.' : 'Choose one answer.';

/**
 * A link the preview may open or frame: a path on this site or an http(s) address.
 * Anything else (javascript:, data:, a protocol-relative //host) gets null.
 */
export function safeMediaUrl(url: string | null | undefined): string | null {
  const u = typeof url === 'string' ? url.trim() : '';
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/') && !u.startsWith('//')) return u;
  return null;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : value == null ? '' : String(value));

function quizOptions(raw: unknown): LmsQuizOption[] {
  let list = raw;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
    // The server marks a quiz with a plain truthiness test on is_correct; so does this.
    .map((o, i) => ({ id: text(o.id) || String(i + 1), text: text(o.text), is_correct: Boolean(o.is_correct) }));
}

/**
 * The slides a learner gets, in the order they get them: active slides only, by
 * display_order (the server already does both; this keeps the preview right if a
 * response ever carries a retired row).
 */
export function previewSlides(rows: unknown): LmsPreviewSlide[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && r.is_active !== false)
    // A missing order goes last, as PostgreSQL's ORDER BY puts NULLs last.
    .map((r, index) => ({
      r,
      index,
      order: r.display_order === null || r.display_order === undefined || r.display_order === ''
        ? Number.POSITIVE_INFINITY
        : Number(r.display_order),
    }))
    .sort((a, b) => {
      const ao = Number.isNaN(a.order) ? Number.POSITIVE_INFINITY : a.order;
      const bo = Number.isNaN(b.order) ? Number.POSITIVE_INFINITY : b.order;
      if (ao !== bo) return ao < bo ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ r, index }) => ({
      id: text(r.id) || `slide-${index}`,
      slide_type: text(r.slide_type),
      title: text(r.title),
      body_text: text(r.body_text),
      media_url: text(r.media_url),
      video_embed_url: text(r.video_embed_url),
      background_color: typeof r.background_color === 'string' ? r.background_color : null,
      quiz_question: text(r.quiz_question),
      quiz_options: quizOptions(r.quiz_options),
      quiz_explanation: text(r.quiz_explanation),
    }));
}
